import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { SERVICES, STATS, INDUSTRIES } from "../lib/content";
import * as Icons from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1618353776413-57c0782c9c87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxmaW5hbmNpYWwlMjBkaXN0cmljdCUyMHNreXNjcmFwZXJ8ZW58MHx8fHwxNzgzMDkyMTU0fDA&ixlib=rb-4.1.0&q=85";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBib2FyZHJvb218ZW58MHx8fHwxNzgzMDkyMTQ5fDA&ixlib=rb-4.1.0&q=85";

const Overline = ({ children }) => (
  <span className="inline-block font-sans text-[11px] md:text-xs uppercase tracking-[0.28em] text-[#C5A880] font-semibold">
    {children}
  </span>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-20 md:pt-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <Overline>Accounting · Advisory · Fractional CFO</Overline>
            <h1 className="mt-8 font-serif text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tighter text-[#0A192F] font-medium">
              Financial clarity. <br />
              <span className="italic text-[#1E293B]">Strategic</span> growth.
            </h1>
            <p className="mt-8 max-w-xl font-sans text-lg text-slate-600 leading-relaxed fade-up fade-up-delay-1">
              We help businesses build stronger financial foundations through expert bookkeeping,
              accounting, cash flow management, financial reporting, and fractional CFO
              services — delivering the clarity needed to make confident business decisions.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 fade-up fade-up-delay-2">
              <Link
                to="/contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-3 bg-[#0A192F] text-white px-8 py-4 font-sans text-[12px] tracking-[0.22em] uppercase hover:bg-[#1E293B] transition-all hover:-translate-y-0.5"
              >
                Book a Consultation
                <ArrowUpRight size={16} className="transition-transform group-hover:rotate-12" strokeWidth={1.5} />
              </Link>
              <Link
                to="/services"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-3 bg-transparent border border-[#0A192F] text-[#0A192F] px-8 py-4 font-sans text-[12px] tracking-[0.22em] uppercase hover:bg-slate-50 transition-all"
              >
                Explore Services
              </Link>
            </div>

            {/* Micro details */}
            <div className="mt-16 grid grid-cols-3 gap-6 fade-up fade-up-delay-3">
              {STATS.slice(0, 3).map((s) => (
                <div key={s.label} className="border-l border-[#C5A880] pl-4">
                  <div className="font-serif text-3xl md:text-4xl text-[#0A192F] tracking-tight">{s.value}</div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 fade-up fade-up-delay-2">
            <div className="relative">
              <div className="absolute -inset-6 border border-[#C5A880]/40 -z-0" aria-hidden />
              <img
                src={HERO_IMG}
                alt="Financial district skyline"
                className="relative w-full h-[520px] md:h-[620px] object-cover hero-image-mask"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-[#0A192F] text-white p-6 md:p-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#C5A880]">Precision Note</div>
                <p className="mt-3 font-serif text-lg md:text-xl leading-snug">
                  “The right numbers, prepared the right way, earn the room before you enter it.”
                </p>
                <p className="mt-3 text-xs text-slate-400 tracking-[0.15em] uppercase">— Ervion Practice</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE / INDUSTRIES */}
      <section className="py-10 border-b border-[#E2E8F0] overflow-hidden bg-white">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...INDUSTRIES, ...INDUSTRIES, ...INDUSTRIES].map((ind, i) => (
            <span
              key={i}
              className="mx-12 font-serif text-3xl md:text-5xl text-slate-300 tracking-tight"
            >
              {ind} <span className="text-[#C5A880] mx-6">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* SERVICES ribbon */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
          <Overline>Comprehensive Financial Solutions</Overline>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tighter font-medium text-[#0A192F] leading-[1.02]">
            Everything you need to build a stronger <span className="italic">financial operation</span> — all in one place.
          </h2>
          <p className="mt-6 font-sans text-slate-600 leading-relaxed max-w-md">
            Whether you&apos;re launching a startup or scaling an established business, our flexible
            services adapt to your needs. Get the right level of support at every stage without the
            cost of a full in-house finance team.
          </p>
          <Link
            to="/services"
            data-testid="home-services-link"
            className="mt-10 inline-flex items-center gap-2 font-sans text-sm tracking-[0.18em] uppercase text-[#0A192F] link-gold"
          >
            Explore Our Services <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </div>

        <div className="lg:col-span-8">
          <ul className="divide-y divide-[#E2E8F0] border-t border-b border-[#E2E8F0]">
            {SERVICES.map((s) => {
              const Icon = Icons[s.icon] || Icons.Circle;
              return (
                <li key={s.id} className="group py-6 md:py-7" data-testid={`home-service-${s.id}`}>
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <span className="col-span-2 md:col-span-1 font-sans text-xs text-[#C5A880] tracking-[0.25em]">
                      {s.number}
                    </span>
                    <div className="col-span-8 md:col-span-9">
                      <div className="flex items-center gap-4">
                        <Icon size={20} strokeWidth={1.5} className="text-[#0A192F] shrink-0" />
                        <h3 className="font-serif text-xl md:text-2xl text-[#0A192F] tracking-tight">
                          {s.title}
                        </h3>
                      </div>
                      <p className="mt-2 md:ml-9 font-sans text-sm md:text-base text-slate-600 max-w-2xl">
                        {s.description}
                      </p>
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <ArrowUpRight
                        size={22}
                        strokeWidth={1.25}
                        className="text-slate-300 group-hover:text-[#C5A880] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ABOUT teaser */}
      <section className="bg-[#F8F9FA] border-y border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={ABOUT_IMG}
                alt="Modern boardroom"
                className="w-full h-[520px] object-cover hero-image-mask"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <Overline>Our Approach</Overline>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tighter font-medium text-[#0A192F] leading-[1.05]">
              Building stronger financial <span className="italic">foundations</span> for growing businesses.
            </h2>
            <p className="mt-8 font-sans text-lg text-slate-600 leading-relaxed max-w-2xl">
              At Ervion, we combine experienced financial professionals with efficient technology to
              deliver accurate accounting, reliable reporting, and strategic financial guidance. Our
              goal is simple: provide the financial clarity and confidence you need to focus on
              growing your business.
            </p>
            <div className="mt-12 grid grid-cols-2 gap-8 max-w-lg">
              <div>
                <div className="font-serif text-5xl text-[#0A192F] tracking-tight">15+</div>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mt-2">Years avg. experience</div>
              </div>
              <div>
                <div className="font-serif text-5xl text-[#0A192F] tracking-tight">100%</div>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mt-2">US GAAP & IFRS ready</div>
              </div>
            </div>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-12 inline-flex items-center gap-2 font-sans text-sm tracking-[0.18em] uppercase text-[#0A192F] link-gold"
            >
              About Ervion <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <Overline>By the Numbers</Overline>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-y-16 gap-x-8">
          {STATS.map((s, i) => (
            <div key={s.label} className="border-t border-[#0A192F] pt-6">
              <div className="font-serif text-5xl md:text-7xl text-[#0A192F] tracking-tighter">
                {s.value}
              </div>
              <div className="mt-4 text-xs uppercase tracking-[0.22em] text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Overline>Ready When You Are</Overline>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-tighter font-medium leading-[1.02]">
              Let&apos;s build the financial foundation <br />
              <span className="italic text-[#C5A880]">your business needs to grow.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center gap-3 bg-[#C5A880] text-[#0A192F] px-10 py-5 font-sans text-[12px] tracking-[0.22em] uppercase hover:bg-white transition-all hover:-translate-y-0.5"
            >
              Start the Conversation <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
